/* src/contexts/PWAUpdateContext.js */
import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';

const PWAUpdateContext = createContext(null);

export const PWAUpdateProvider = ({ children }) => {
    const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
    const [waitingWorker, setWaitingWorker] = useState(null);

    useEffect(() => {
        // Check if service workers are supported
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.getRegistration().then(reg => {
                if (!reg) {
                    console.log('[PWA Update] No service worker registration found.');
                    return;
                }
                console.log('[PWA Update] Service worker registration found:', reg);

                // 1. Check for waiting worker immediately on load
                if (reg.waiting) {
                    console.log('[PWA Update] Waiting worker found on initial load.');
                    setWaitingWorker(reg.waiting);
                    setIsUpdateAvailable(true);
                }

                // 2. Listen for new worker installing
                reg.addEventListener('updatefound', () => {
                    const newWorker = reg.installing;
                    console.log('[PWA Update] updatefound event: New worker installing.', newWorker);

                    if (newWorker) {
                        newWorker.addEventListener('statechange', () => {
                            console.log('[PWA Update] New worker statechange:', newWorker.state);
                            // Check if the new worker has installed and is waiting
                            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                                console.log('[PWA Update] New worker installed and waiting.');
                                setWaitingWorker(newWorker); // Store the waiting worker
                                setIsUpdateAvailable(true); // Set flag to show prompt/modal
                            }
                        });
                    }
                });
            }).catch(error => {
                console.error('[PWA Update] Error getting service worker registration:', error);
            });

            // 3. Listen for controller change (new worker activated)
            let refreshing = false;
            navigator.serviceWorker.addEventListener('controllerchange', () => {
                console.log('[PWA Update] controllerchange event detected.');
                if (!refreshing) {
                    console.log('[PWA Update] Reloading page to use new service worker...');
                    refreshing = true; // Prevent potential multiple reloads
                    window.location.reload();
                }
            });

        } else {
            console.log('[PWA Update] Service workers not supported.');
        }
    }, []); // Run only once on mount

    // Function to activate the update
    const activateUpdate = useCallback(() => {
        if (waitingWorker) {
            console.log('[PWA Update] Sending SKIP_WAITING message to waiting worker.');
            // Send message to the waiting service worker
            waitingWorker.postMessage({ type: 'SKIP_WAITING' });
            // Hide the update available indicator immediately
            setIsUpdateAvailable(false);
            // The 'controllerchange' listener above will handle the reload.
        } else {
            console.warn('[PWA Update] activateUpdate called but no waiting worker found.');
            setIsUpdateAvailable(false); // Hide prompt anyway if worker disappeared
        }
    }, [waitingWorker]); // Dependency on waitingWorker state

    const value = {
        isUpdateAvailable,
        activateUpdate,
    };

    return (
        <PWAUpdateContext.Provider value={value}>
            {children}
        </PWAUpdateContext.Provider>
    );
};

// Custom hook
export const usePWAUpdate = () => {
    const context = useContext(PWAUpdateContext);
    if (context === undefined) {
        throw new Error('usePWAUpdate must be used within a PWAUpdateProvider');
    }
    return context || { isUpdateAvailable: false, activateUpdate: () => console.warn("PWAUpdateContext not ready") };
};